import { Input, Button, Popover } from "antd";
import { useContext, useEffect, useState } from "react";
import { BlingsBtn } from "../antd-extensions/blings-btn.component";
import { AsyncOpState } from "../../types/enums/async-op-states";
import _ from "lodash";
import { filterObjectBySchema } from "../../helpers/jsonSchema.helpers";
import { OverviewPageContext } from "../../view/ProjectDemoPage/OverviewPage";
import "./BlingsNewDataInput.scss";

interface Props {
  hasDataPoints: boolean;
  setIsModalOpen: React.Dispatch<
    React.SetStateAction<{ modalOpen: boolean; mode: "create" | "edit" }>
  >;
  creatingNewDataPoint: boolean;
  setCreatingNewDatapoint: (input: boolean) => void;
  hasFormDataChanged: boolean;
  dataInputProps: {
    recordChanged: boolean;
    selectedDataPointId: string | undefined;
    setSelectedDataPointId: React.Dispatch<
      React.SetStateAction<string | undefined>
    >;
    setCurrentFormData: (formData: object) => void;
    savedFormData: object;
    currentFormData: object | null;
    schema: object;
  };
}

export function BlingsNewDataInput({
  hasDataPoints,
  hasFormDataChanged,
  setIsModalOpen,
  creatingNewDataPoint,
  setCreatingNewDatapoint,
  dataInputProps,
}: Props) {
  // Context definitions
  const { project, dataPointsFromCSV, dataPointsFromManual } =
    useContext(OverviewPageContext);

  const {
    selectedDataPointId,
    setSelectedDataPointId,
    currentFormData,
    savedFormData,
    schema,
    setCurrentFormData,
  } = dataInputProps;

  // State definitions
  const [dataPointInput, setDataPointInput] = useState<string>(
    selectedDataPointId || ""
  );

  useEffect(() => {
    setDataPointInput(selectedDataPointId || "");
  }, [selectedDataPointId]);

  const renderButton = () => {
    const isFromCSV = Object.keys(dataPointsFromCSV || {}).includes(
      dataPointInput
    );
    const buttonElement = (
      <BlingsBtn
        opState={project.createNewDPStatus}
        className="dp-submit"
        htmlType={"submit"}
        btnTexts={{
          [AsyncOpState.Error]: "Error Creating Data",
          [AsyncOpState.Saving]: "Creating",
          [AsyncOpState.Untouched]: "Create",
          [AsyncOpState.Changed]:
            isFromCSV ||
            Object.keys(dataPointsFromManual || {}).includes(dataPointInput)
              ? "Save"
              : "Create",
          [AsyncOpState.Success]: "DONE",
        }}
        disabled={
          (!creatingNewDataPoint && !hasFormDataChanged) ||
          !dataPointInput ||
          isFromCSV
        }
        onClick={async () => {
          try {
            const final_data = { ...currentFormData };
            const filteredFormData = filterObjectBySchema(final_data, schema);
            await project.createDataPoint(filteredFormData, dataPointInput);
            setSelectedDataPointId(dataPointInput);
            if (
              !(
                Object.keys(dataPointsFromCSV || {}).includes(dataPointInput) ||
                Object.keys(dataPointsFromManual || {}).includes(dataPointInput)
              )
            ) {
              setCreatingNewDatapoint(false);
            }
          } catch (e: any) {
            console.error("err", e);
          }
        }}
      />
    );

    if (isFromCSV) {
      return (
        <Popover
          className="tooltip-1"
          placement="rightTop"
          overlayInnerStyle={{ width: "14rem" }}
          content={
            <div style={{ width: "10rem" }}>
              This data point is not editable because it was uploaded from a CSV
              file. To modify it, please update the original CSV and re-upload.
            </div>
          }
        >
          <div>{buttonElement}</div>
        </Popover>
      );
    }
    return buttonElement;
  };

  useEffect(() => {
    if (!selectedDataPointId && !creatingNewDataPoint && !hasFormDataChanged) {
      setDataPointInput("");
      setCreatingNewDatapoint(true);
    }
  }, [selectedDataPointId, creatingNewDataPoint, hasFormDataChanged]);

  return (
    <div className="dp-view">
      {!selectedDataPointId && !creatingNewDataPoint && hasFormDataChanged && (
        <div className="dp-field">
          <Input
            value={dataPointInput}
            onChange={(e) => setDataPointInput(e.target.value)}
            placeholder="Name this version"
            className="dp-input"
          />
          <Button
            onClick={() => {
              if (hasDataPoints) {
                setCreatingNewDatapoint(false);
              } else {
                setCreatingNewDatapoint(false);
              }

              if (dataPointInput === "") {
                setIsModalOpen({ modalOpen: false, mode: "create" });
              }

              setCurrentFormData(savedFormData);
            }}
            className="opaque-button"
          >
            Cancel
          </Button>
          {renderButton()}
        </div>
      )}

      {!selectedDataPointId && creatingNewDataPoint && (
        <div className="dp-field">
          <Input
            value={dataPointInput}
            onChange={(e) => setDataPointInput(e.target.value)}
            placeholder="Name this version"
            className="dp-input"
          />
          <Button
            onClick={() => {
              if (hasDataPoints) {
                setCreatingNewDatapoint(false);
                setCurrentFormData(savedFormData);
              } else {
                setCreatingNewDatapoint(false);
              }
              if (dataPointInput === "") {
                setIsModalOpen({ modalOpen: false, mode: "create" });
              }
            }}
            className="opaque-button"
          >
            Cancel
          </Button>
          {renderButton()}
        </div>
      )}

      {selectedDataPointId && creatingNewDataPoint && (
        <div className="dp-field">
          <Input
            value={dataPointInput}
            onChange={(e) => setDataPointInput(e.target.value)}
            placeholder="Name this version"
            className="dp-input"
          />
          <Button
            onClick={() => {
              if (hasDataPoints) {
                setCreatingNewDatapoint(false);
                setCurrentFormData(savedFormData);
                setDataPointInput(selectedDataPointId);
              } else {
                setIsModalOpen({ modalOpen: false, mode: "create" });
              }
              if (dataPointInput === "") {
                setIsModalOpen({ modalOpen: false, mode: "create" });
              }
            }}
            className="opaque-button"
          >
            Cancel
          </Button>
          {renderButton()}
        </div>
      )}

      {selectedDataPointId && !creatingNewDataPoint && !hasFormDataChanged && (
        <div className="dp-field">
          <Button
            onClick={() => {
              setDataPointInput("");
              setCreatingNewDatapoint(true);
            }}
            className="opaque-button"
          >
            Duplicate
          </Button>
          <div className="dp-field-end">{renderButton()}</div>
        </div>
      )}

      {selectedDataPointId && !creatingNewDataPoint && hasFormDataChanged && (
        <div className="dp-field">
          <Button
            onClick={() => {
              setDataPointInput("");
              setCreatingNewDatapoint(true);
            }}
            className="opaque-button"
          >
            Duplicate
          </Button>

          <div className="dp-field-end">
            <Button
              onClick={() => {
                if (hasDataPoints) {
                  setCreatingNewDatapoint(false);
                  setCurrentFormData(savedFormData);
                  setDataPointInput(selectedDataPointId);
                } else {
                  setIsModalOpen({ modalOpen: false, mode: "create" });
                }
                if (dataPointInput === "") {
                  setIsModalOpen({ modalOpen: false, mode: "create" });
                }
              }}
              className="opaque-button"
            >
              Cancel
            </Button>
            {renderButton()}
          </div>
        </div>
      )}
    </div>
  );
}
